import React from "react";

/**
 * 페이지 섹션 래퍼.
 *
 * 비즈니스 페이지 7개의 섹션 13곳이 같은 뼈대를 각자 적어 갖고 있었다.
 *
 *   <section className="bg-gray-white pb-8 lg:pb-36">
 *     <div className="container max-w-full overflow-hidden bg-gray-50 px-5 pb-16 pt-10 xl:px-0">
 *
 * 좌우 여백(px-5 xl:px-0)과 overflow-hidden 은 13곳 전부 같았고, 세로 여백만
 * py-10 / pt-10 pb-16 / py-[100px] / py-12 md:py-[100px] 처럼 제각각이었다.
 * 그 값을 space 세 단계로 좁힌다.
 *
 * 참고로 기존 마크업의 bg-gray-white 는 Tailwind 에 없는 이름이라 아무 효과가
 * 없었다. 여기서는 받지 않는다.
 */

const BG = {
    none: "",
    white: "bg-white",
    gray: "bg-gray-50"
} as const;

const WIDTH = {
    full: "max-w-full",
    "6xl": "max-w-6xl"
} as const;

/** tailwind.config.js 의 spacing 토큰과 짝을 이룬다. */
const SPACE = {
    none: "",
    sm: "py-section-sm", //  24 → 48
    md: "py-section", //  40 → 96
    lg: "py-section-lg" //  56 → 128
} as const;

/**
 * 위 여백만 따로 지정할 때 쓴다. Tailwind 는 py 보다 pt 를 뒤에 내보내므로
 * py-* 와 함께 써도 이쪽이 이긴다.
 */
const SPACE_TOP = {
    none: "pt-0",
    sm: "pt-section-sm",
    md: "pt-section",
    lg: "pt-section-lg"
} as const;

type SectionProps = {
    /** 섹션 배경. 기본은 배경 없음(부모 배경이 비친다). */
    bg?: keyof typeof BG;
    /** 콘텐츠 최대 폭. */
    width?: keyof typeof WIDTH;
    /** 세로 여백 단계. */
    space?: keyof typeof SPACE;
    /**
     * 위 여백만 따로 지정한다. 앞 섹션의 아래 여백과 더해져 두 배로
     * 벌어질 때 "none" 을 줘서 한쪽만 남긴다.
     */
    spaceTop?: keyof typeof SPACE_TOP;
    /** 앵커 링크용 id (서브탭이 #1 등으로 참조한다). */
    id?: string;
    /** 섹션 고유의 추가 클래스. 여백은 여기 넣지 말고 space 를 쓴다. */
    className?: string;
    children: React.ReactNode;
};

const Section = ({
    bg = "none",
    width = "full",
    space = "md",
    spaceTop,
    id,
    className = "",
    children
}: SectionProps) => (
    <section
        id={id}
        /* id 가 붙은 섹션은 앵커(#1) 대상이다. 비즈니스 페이지의 고정 헤더와
           sticky 서브탭이 덮는 만큼 착지 지점을 내린다. */
        className={`${BG[bg]} ${
            id ? "scroll-mt-below-tabs md:scroll-mt-below-tabs-md" : ""
        }`}
    >
        <div
            className={`container m-auto overflow-hidden px-5 xl:px-0 ${WIDTH[width]} ${SPACE[space]} ${spaceTop ? SPACE_TOP[spaceTop] : ""} ${className}`}
        >
            {children}
        </div>
    </section>
);

export default Section;
